import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@apollo/react-hooks'
import { FileDown } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import * as Query from '../../../api/gql/query'
import { useModuleActions } from '../../../hooks/useMyPermissions'
import { costReportDownloadUrl, type ReportFormat } from '../lib/download'
import { describeCostControlError } from '../lib/errors'
import { moneyLocale } from '../lib/money'
import type { CostOverview } from '../types'
import BillingByMonthCard from './BillingByMonthCard'
import BillingWarningsCard from './BillingWarningsCard'
import OverviewStats from './OverviewStats'
import OverviewTables from './OverviewTables'
import OverviewTabSkeleton from './OverviewTabSkeleton'
import ProjectHistoryCard from './ProjectHistoryCard'
import TrendChart from './TrendChart'

const ALL = '__all__'

interface Props { projectId: string; onOpenPending: () => void }

/** 1b-C spec §4: the whole project or one building at a glance, with the report download and the project history. */
const OverviewTab: React.FC<Props> = ({ projectId, onOpenPending }) => {
  const { t, i18n } = useTranslation()
  const locale = moneyLocale(i18n.language)
  const { canExport } = useModuleActions('COST_CONTROL', projectId)
  const [nodeId, setNodeId] = useState<string | null>(null)
  const { data, loading, error, refetch } = useQuery<{ costOverview: CostOverview }>(Query.COST_OVERVIEW, {
    variables: { projectId, nodeId },
    fetchPolicy: 'cache-and-network',
    context: { handlesErrors: true },
  })
  const view = data?.costOverview

  if (!view && loading) return <OverviewTabSkeleton />
  if (!view) {
    return (
      <div className="flex flex-col items-center gap-3 py-16 text-sm text-muted-foreground">
        <p>{describeCostControlError(error?.message, t)}</p>
        {/* A building deleted meanwhile cannot be retried: fall back to the whole project. */}
        <Button variant="outline" size="sm" className="h-8" onClick={() => { if (nodeId) setNodeId(null); else refetch().catch(() => undefined) }}>
          {t('CostControl_retry')}
        </Button>
      </div>
    )
  }

  const download = (format: ReportFormat) => { window.open(costReportDownloadUrl(projectId, format, nodeId), '_blank') }
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3 py-2">
        <Select value={nodeId ?? ALL} onValueChange={(v) => setNodeId(v === ALL ? null : v)}>
          <SelectTrigger className="h-8 w-auto min-w-[200px] text-sm"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>{t('CostControl_overview_scope_all')}</SelectItem>
            {view.scopes.map((s) => <SelectItem key={s.nodeId} value={s.nodeId}>{s.code ? `${s.code} · ${s.name}` : s.name}</SelectItem>)}
          </SelectContent>
        </Select>
        {canExport && (
          <div className="ml-auto">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="h-8">
                  <FileDown className="h-4 w-4" />
                  {t('CostControl_overview_report')}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => download('pdf')}>{t('CostControl_overview_report_pdf')}</DropdownMenuItem>
                <DropdownMenuItem onClick={() => download('xlsx')}>{t('CostControl_export_xlsx')}</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        )}
      </div>
      <OverviewStats totals={view.totals} currency={view.currency} locale={locale} />
      <TrendChart points={view.trend} currency={view.currency} locale={locale} />
      {view.billing && !nodeId && <BillingByMonthCard projectId={projectId} currency={view.currency} locale={locale} language={i18n.language} />}
      {view.billing && <BillingWarningsCard billing={view.billing} currency={view.currency} locale={locale} />}
      <OverviewTables view={view} showScopes={!nodeId} locale={locale} onOpenPending={onOpenPending} />
      <ProjectHistoryCard projectId={projectId} currency={view.currency} locale={locale} />
    </div>
  )
}
export default OverviewTab
